import { ConversationListPages, keepFirstConversationPage } from 'dataQueries/conversationListCache'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { closedConversationPagesKey, conversationMessagesKey } from 'dataQueries/conversations.query'
import { hasConversationLanguage, isConversationClosedNotice, patchConversationListData, patchConversationThreadData, patchConversationThreadOnReadReceipt } from './conversationCache'
import { useEffect, useRef } from 'react'

import { ConversationListCacheData, mapConversationListCache } from 'dataQueries/conversationListCache'
import { ConversationMessageType } from 'models/conversations'
import { useChatWidget } from '../ChatWidget.context'
import { useConversationSocket } from 'utils/conversationSocket'
import { useQueryClient } from '@tanstack/react-query'
import { useResolvedCurrentUserId } from './useResolvedCurrentUserId'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Delays (ms) after a conversation-created event at which we re-fetch the list, to reliably pick up
 * the conversation's first message — which is persisted shortly AFTER the creation event fires.
 */
const FIRST_MESSAGE_REFETCH_DELAYS_MS = [1_200, 4_000]

/**
 * Wires the conversation list AND the open thread to the real-time socket:
 *  - subscribes to the topics of the given conversations,
 *  - patches every conversation-list cache entry when a new message broadcast arrives
 *    (preview, timestamp, unread count, move-to-top),
 *  - appends / upserts the new message in the thread cache so the bubble appears instantly, replaying that
 *    upsert once the thread's own first fetch lands when the cache was not hydrated yet,
 *  - upgrades outgoing-message status to READ when another participant moves their read cursor.
 *
 * Notifications are intentionally NOT raised here — they're driven off the conversation-list unread
 * counts in `useUnreadNotifications`, which is reliable even when a brand-new conversation's first
 * message broadcast is missed during the STOMP subscribe round-trip.
 * Presence is handled inside the socket provider and exposed separately via `useConversationSocket`.
 */
export const useConversationsRealtime = (conversationIds: string[]) => {
  const queryClient = useQueryClient()
  const {
    subscribeTo,
    subscribeToConversation,
    subscribeToUserChannel,
    registerMessageHandler,
    registerReadReceiptHandler,
    registerConversationCreatedHandler,
    registerConversationUpdatedHandler,
    isConnected,
    socketUserId,
  } = useConversationSocket()
  const resolvedCurrentUserId = useResolvedCurrentUserId()
  const { baseUserData } = useUserData()
  // The broker only lets a session subscribe to its own personal topic and closes the whole session on a
  // rejected SUBSCRIBE, so this id must be authoritative: never the participant-scan guess, which picks the
  // first participant holding the caller's role (for an admin, often the assistant) (QA-384).
  const personalTopicUserId = socketUserId ?? baseUserData?.internalId
  const { onScreenConversationId } = useChatWidget()

  // Keep mutable values in refs so the single registered handler always reads current values. The
  // resolved id is derived from the cached conversation's `participants[]`, which is the same id
  // the backend stamps on `senderId` — preferring it over `baseUserData.internalId` makes the
  // own-message / read-receipt comparisons robust when the userMeData payload omits internalId.
  const currentUserIdRef = useRef(resolvedCurrentUserId)
  currentUserIdRef.current = resolvedCurrentUserId
  // A conversation only counts as "open" — and thus exempt from unread-badge bumps — while its thread is
  // on screen: widget open on it AND tab visible. A lingering `selectedConversationId` (widget closed,
  // popover back on its list) or a hidden tab must still bump the badge, which is also what drives the
  // desktop notification in `useUnreadNotifications` (QA-398).
  const openConversationIdRef = useRef<string | null>(null)
  openConversationIdRef.current = onScreenConversationId

  // Keep a stable ref so the subscribe effect always reads the current ids even when triggered
  // by an isConnected flip (not a conversationIds change) — prevents the stale-closure anti-pattern.
  const conversationIdsRef = useRef(conversationIds)
  conversationIdsRef.current = conversationIds

  // (Re)subscribe whenever the visible conversations change or the socket (re)connects.
  const idsKey = conversationIds.join(',')
  useEffect(() => {
    subscribeTo(conversationIdsRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, isConnected, subscribeTo])

  // Subscribe to the user's personal topic so a brand-new conversation (where we're not yet subscribed
  // to its topic) still notifies us — see the created-handler below.
  useEffect(() => {
    subscribeToUserChannel(personalTopicUserId)
  }, [personalTopicUserId, isConnected, subscribeToUserChannel])

  // On (re)connect, refetch the conversation list so any conversation created while we were
  // disconnected — or before our personal-topic subscription was wired — surfaces immediately.
  // The conversation-created event is a one-shot with no replay, so a recipient that wasn't yet
  // subscribed (e.g. a creative whose id resolves only once a conversation exists) would otherwise
  // only discover it on the next poll. Pairs with the conversation-list `refetchInterval` fallback.
  useEffect(() => {
    if (isConnected) {
      void queryClient.invalidateQueries({ queryKey: [EntityKeys.CONVERSATION, QueryType.LIST] })
    }
  }, [isConnected, queryClient])

  // On a conversation-created event: subscribe to the new conversation's topic immediately, and refetch
  // the list now AND a moment later. The backend publishes this event when the conversation is created
  // — BEFORE its first message is persisted (creation and the first message are two separate calls) — so
  // the immediate refetch only sees the empty shell (unreadCount=0). The staggered re-fetches pick up the
  // first message (lighting the unread badge + the list-driven notification) even if its real-time
  // broadcast was missed during the STOMP subscribe round-trip. The 15s poll is the final fallback.
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = []
    const invalidateList = () =>
      void queryClient.invalidateQueries({ queryKey: [EntityKeys.CONVERSATION, QueryType.LIST] })

    const unregister = registerConversationCreatedHandler((event) => {
      subscribeToConversation(event.conversationId)
      // The thread needs the same staggered catch-up as the list: a missed first-message broadcast
      // otherwise lights the unread badge (list-driven) while the open thread stays empty.
      const refresh = () => {
        invalidateList()
        void queryClient.invalidateQueries({ queryKey: conversationMessagesKey(event.conversationId) })
      }
      refresh()
      FIRST_MESSAGE_REFETCH_DELAYS_MS.forEach((delay) => timers.push(setTimeout(refresh, delay)))
    })

    return () => {
      timers.forEach(clearTimeout)
      unregister()
    }
  }, [registerConversationCreatedHandler, subscribeToConversation, queryClient])

  // Register the new-message cache patcher once.
  useEffect(() => {
    return registerMessageHandler((message) => {
      const listFilter = { queryKey: [EntityKeys.CONVERSATION, QueryType.LIST] }
      queryClient.setQueriesData(
        listFilter,
        patchConversationListData(message, currentUserIdRef.current, openConversationIdRef.current)
      )
      if (message.messageType === ConversationMessageType.SYSTEM) {
        const lists = queryClient.getQueriesData(listFilter).map(([, data]) => data)
        if (isConversationClosedNotice(message) || !hasConversationLanguage(lists, message.conversationId)) {
          if (isConversationClosedNotice(message)) {
            queryClient.setQueryData<ConversationListPages>(closedConversationPagesKey(), keepFirstConversationPage)
          }
          void queryClient.invalidateQueries(listFilter)
        }
      }
      const threadKey = conversationMessagesKey(message.conversationId)
      const patchThread = () => queryClient.setQueryData(threadKey, patchConversationThreadData(message))

      if (patchThread()) return

      // No page to patch. When the thread's own first fetch is in flight, that request was issued before
      // this message existed and cannot return it — and an invalidation is deduped onto it by React Query
      // (`Query.fetch` returns the in-flight promise while `data` is undefined), which is how the message
      // used to be stranded. Replay the upsert when the response lands instead; it is keyed on id /
      // clientMessageId, so a response that already carries the message is patched in place, not doubled.
      // Nothing in flight ⇒ the thread is not mounted and fetches from scratch on its next mount.
      const inFlight = queryClient.getQueryCache().find({ queryKey: threadKey })?.promise
      if (inFlight) void inFlight.then(patchThread, () => undefined)
    })
  }, [registerMessageHandler, queryClient])

  useEffect(() => {
    return registerConversationUpdatedHandler((event) => {
      const listFilter = { queryKey: [EntityKeys.CONVERSATION, QueryType.LIST] }
      queryClient.setQueriesData<ConversationListCacheData>(listFilter, (old) => mapConversationListCache(old, (content) =>
        content.some((conversation) => conversation.id === event.conversationId)
          ? content.map((conversation) => conversation.id === event.conversationId
            ? {
              ...conversation,
              supportStatus: event.supportStatus,
              ...(event.supportStatusChangedAt !== undefined && { supportStatusChangedAt: event.supportStatusChangedAt }),
              ...(event.status !== undefined && { status: event.status }),
              ...(event.reopenedAt !== undefined && { reopenedAt: event.reopenedAt }),
            }
            : conversation)
          : content
      ))
      void queryClient.invalidateQueries(listFilter)
    })
  }, [registerConversationUpdatedHandler, queryClient])

  // Register the read-receipt cache patcher once.
  useEffect(() => {
    return registerReadReceiptHandler((receipt) => {
      // The backend echoes the caller's own receipt on the shared conversation topic. Ignoring it
      // here prevents incorrectly bumping the caller's outgoing DELIVERED messages to READ (the
      // counterparty hasn't read them — we just acknowledged the counterparty's last message).
      if (receipt.userId === currentUserIdRef.current) return
      queryClient.setQueryData(
        conversationMessagesKey(receipt.conversationId),
        patchConversationThreadOnReadReceipt(receipt, currentUserIdRef.current)
      )
    })
  }, [registerReadReceiptHandler, queryClient])
}
